import { useState } from 'react';
import { FileStoreConnection } from 'models/Connection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCopy } from '@fortawesome/free-solid-svg-icons';
import { Alert, Button } from 'react-bootstrap';
import CopyableCodeBlock from 'components/common/CopyableCodeBlock';
import {
  DataAccessSectionHeading,
  DataAccessTabHeader,
  DataAccessText,
} from 'shiplab-components/data-access/dataAccessTypography';

const DEFAULT_AWS_REGION = 'us-east-1';

type S3Location = {
  bucket: string;
  prefix: string;
};

const parseS3Location = (invoiceFormat?: string | null): S3Location | null => {
  if (!invoiceFormat) {
    return null;
  }

  const match = invoiceFormat.match(/^s3:\/\/([^/]+)\/(.*)$/);
  if (!match) {
    return null;
  }

  return {
    bucket: match[1],
    prefix: match[2],
  };
};

const buildS3EnvCommands = (
  accessKeyId: string,
  secretAccessKey: string,
  region = DEFAULT_AWS_REGION,
): string =>
  [
    `export AWS_ACCESS_KEY_ID="${accessKeyId}"`,
    `export AWS_SECRET_ACCESS_KEY="${secretAccessKey}"`,
    `export AWS_DEFAULT_REGION="${region}"`,
  ].join('\n');

const buildS3ConfigureCommand = (region = DEFAULT_AWS_REGION): string =>
  `aws configure set region ${region}`;

const buildS3GetDataCommands = (bucket?: string, prefix?: string): string => {
  const invoicesPrefix = prefix?.split('/')[0] ?? 'invoices';
  const bucketPath = bucket ?? '<your-bucket>';

  return [
    '# List your invoice files',
    `aws s3 ls s3://${bucketPath}/${invoicesPrefix}/ --recursive`,
    '',
    '# Download a file — copy the path from the list above',
    `aws s3 cp s3://${bucketPath}/${invoicesPrefix}/<path-to-file> ./invoice-file.csv`,
  ].join('\n');
};

type ConnectionFileStoreProps = {
  connections: FileStoreConnection[];
  invoiceFormat?: string | null;
};

const ConnectionFileStore = ({ connections, invoiceFormat }: ConnectionFileStoreProps) => {
  const [showSecret, setShowSecret] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);

  const connection = connections[0];
  const s3Location = parseS3Location(invoiceFormat);
  const accessKeyId = connection?.aws_user_secret_id ?? '<YOUR_ACCESS_KEY_ID>';
  const secretAccessKey = connection?.aws_user_token ?? '<YOUR_SECRET_ACCESS_KEY>';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setNotification('Copied to clipboard!');
    setTimeout(() => setNotification(null), 2000);
  };

  return (
    <div>
      {notification && (
        <Alert variant="outline-success" onClose={() => setNotification(null)} dismissible>
          {notification}
        </Alert>
      )}

      <DataAccessTabHeader title="Data Lake Access">
        Your carrier invoice files are stored in a dedicated, read-only data lake (S3 bucket). Use
        the conectors below with the AWS CLI or any S3-compatible tool to list and download your
        data. Access is limited to <span className="font-monospace">ListBucket</span> and{' '}
        <span className="font-monospace">GetObject</span> on your bucket only.
      </DataAccessTabHeader>

      {!connection ? (
        <Alert variant="outline-warning">
          Data lake conectors are not available yet. Contact support if you expect access to be
          provisioned.
        </Alert>
      ) : (
        <>
          <DataAccessSectionHeading>Your Conectors</DataAccessSectionHeading>
          <div
            style={{
              display: 'inline-grid',
              gridTemplateColumns: 'auto auto auto',
              columnGap: '0.375rem',
              rowGap: '0.5rem',
              alignItems: 'center',
            }}
          >
            <div>
              <span className="text-900 fw-semi-bold">AWS Access Key ID: </span>
              <span className="font-monospace">{connection.aws_user_secret_id}</span>
            </div>
            <span aria-hidden="true" style={{ width: '2.375rem' }} />
            <Button
              variant="outline-success"
              size="sm"
              className="flex-shrink-0 px-0 d-inline-flex align-items-center justify-content-center"
              style={{ width: '2.375rem' }}
              onClick={(e) => {
                copyToClipboard(connection.aws_user_secret_id);
                e.currentTarget.blur();
              }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </Button>

            <div>
              <span className="text-900 fw-semi-bold">AWS Secret Access Key: </span>
              <span className="font-monospace">
                {showSecret ? connection.aws_user_token : '••••••••••••'}
              </span>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              className="flex-shrink-0 px-0 d-inline-flex align-items-center justify-content-center"
              style={{ width: '2.375rem' }}
              onClick={() => setShowSecret((prev) => !prev)}
              aria-label={showSecret ? 'Hide secret access key' : 'Show secret access key'}
            >
              <FontAwesomeIcon icon={showSecret ? faEyeSlash : faEye} />
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              className="flex-shrink-0 px-0 d-inline-flex align-items-center justify-content-center"
              style={{ width: '2.375rem' }}
              onClick={(e) => {
                copyToClipboard(connection.aws_user_token);
                e.currentTarget.blur();
              }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </div>
          <DataAccessText className="text-700 mt-2 mb-4">
            Default region: <span className="font-monospace">{DEFAULT_AWS_REGION}</span>
          </DataAccessText>
        </>
      )}

      {invoiceFormat && (
        <>
          <DataAccessSectionHeading className="mb-2">Bucket &amp; file layout</DataAccessSectionHeading>
          <DataAccessText className="text-700 mb-2">
            Invoices are organized under your dedicated bucket using this path pattern:
          </DataAccessText>
          <CopyableCodeBlock code={invoiceFormat} />
          {s3Location && (
            <ul className="text-700 mb-4 ps-3" style={{ fontSize: '0.875rem', lineHeight: 1.65 }}>
              <li>
                <span className="fw-semi-bold">Bucket:</span>{' '}
                <span className="font-monospace">{s3Location.bucket}</span>
              </li>
              <li>
                <span className="fw-semi-bold">Prefix:</span>{' '}
                <span className="font-monospace">{s3Location.prefix}</span>
              </li>
            </ul>
          )}
        </>
      )}

      <DataAccessSectionHeading className="mb-2">Quick start with AWS CLI</DataAccessSectionHeading>
      <DataAccessText>
        Install the{' '}
        <a href="https://aws.amazon.com/cli/" target="_blank" rel="noreferrer">
          AWS CLI
        </a>{' '}
        if needed, then run these commands in your terminal.
      </DataAccessText>

      <CopyableCodeBlock
        title="Step 1 — Set conectors"
        description="Run in your shell session, or add to your profile for persistence."
        code={buildS3EnvCommands(accessKeyId, secretAccessKey)}
      />

      <CopyableCodeBlock
        title="Step 2 — Set region"
        description="Only needed if you use aws configure instead of environment variables."
        code={buildS3ConfigureCommand()}
      />

      <CopyableCodeBlock
        title="Step 3 — Get your data"
        description="List files in your bucket, then download one by copying its path from the output."
        code={buildS3GetDataCommands(s3Location?.bucket, s3Location?.prefix)}
      />

      {!invoiceFormat && connection && (
        <Alert variant="outline-info" className="fs-9 mb-0">
          Your bucket path is not available in your profile yet. You can still use the conectors
          above once you know your bucket name — contact support if you need the full S3 path.
        </Alert>
      )}
    </div>
  );
};

export default ConnectionFileStore;
